import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type NotificationType = 'success' | 'error' | 'info';

interface NotificationContextType {
    message: string | null;
    type: NotificationType;
    notify: (msg: string, type?: NotificationType) => void;
    clearNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [message, setMessage] = useState<string | null>(null);
    const [type, setType] = useState<NotificationType>('info');

    const notify = useCallback((msg: string, t: NotificationType = 'info') => {
        setMessage(msg);
        setType(t);
        // Auto-clear after 5 seconds
        setTimeout(() => {
            setMessage((prev) => (prev === msg ? null : prev));
        }, 5000);
    }, []);

    const clearNotification = useCallback(() => {
        setMessage(null);
    }, []);

    return (
        <NotificationContext.Provider value={{ message, type, notify, clearNotification }}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};
